import { Box, Chip, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useHarnessStore } from '@/store/useHarnessStore';
import { formatDecoded } from '@/features/harness/utils/formatDecoded';

/**
 * Renders the live communication frame buffer from local state — the frontend is the one
 * talking to the device over WebUSB and is therefore the source of the live data (no backend
 * involvement, FR-6/FR-7). Sent and received frames are visually distinct (AC1). Decoded content
 * is shown with the same single-line, truncated treatment as the persisted Batch-review
 * Communication Log (`CommunicationLogView`) so the two views are consistent regardless of
 * whether the Batch is still active or already reviewed.
 */
export function LiveCommunicationLog() {
  const liveFrames = useHarnessStore((s) => s.liveFrames);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Live Communication ({liveFrames.length})
      </Typography>

      {liveFrames.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No communication yet.
        </Typography>
      ) : (
        <List dense disablePadding sx={{ maxHeight: 300, overflow: 'auto' }}>
          {liveFrames
            .slice()
            .reverse()
            .map((frame) => {
              const decoded = formatDecoded(frame.decoded);
              return (
                <ListItem key={frame.id} disableGutters sx={{ alignItems: 'flex-start' }}>
                  <Chip
                    size="small"
                    label={frame.direction === 'sent' ? 'Sent' : 'Received'}
                    color={frame.direction === 'sent' ? 'primary' : 'secondary'}
                    sx={{ mr: 1.5, mt: 0.25, minWidth: 80 }}
                  />
                  {!frame.crcValid && (
                    <Chip
                      size="small"
                      label="CRC error"
                      color="error"
                      variant="outlined"
                      sx={{ mr: 1.5, mt: 0.25 }}
                    />
                  )}
                  <ListItemText
                    primary={`${frame.rawBytes.length} bytes`}
                    secondary={
                      <>
                        {new Date(frame.timestamp).toLocaleTimeString()}
                        {decoded && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              display: 'block',
                              fontFamily: 'monospace',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={decoded}
                          >
                            {decoded}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
        </List>
      )}
    </Box>
  );
}
